import React, { useEffect, useState } from 'react'
import { Modal, Pagination, Dropdown, Menu, Button, message, Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import styles from './Users.module.less'
import { useAppDispatch, useAppSelector } from '~/Store'
import { UsersActions } from '~/Store/AdminUser'
import { getListUsers } from '~/Store/AdminUser/selectors'
import { UserResponse, UserService as AdminUserService } from '~/Services/AdminUsers'
import { getListRoles, getRolesToUserId } from '~/Store/Role/selectors'
import { RolesActions } from '~/Store/Role'
import { CaretDownOutlined, DeleteOutlined, MoreOutlined } from '@ant-design/icons'

export const AdminUsers = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const usersResponse = useAppSelector(getListUsers)
  const users = usersResponse?.results ?? []
  const totalUsers = usersResponse?.queryCount ?? 0

  const rolesUserID = useAppSelector(getRolesToUserId)

  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const roleAdminData = useAppSelector(getListRoles)
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  useEffect(() => {
    dispatch(RolesActions.getRolesRequest())
    dispatch(
      UsersActions.getUsersRequest({
        page: currentPage,
        pageSize: pageSize,
      }),
    )
  }, [dispatch, currentPage, pageSize])

  const getInvitationStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'orange'
      case 'Accepted':
        return 'green'
      case 'Rejected':
        return 'red'
      default:
        return 'gray'
    }
  }

  const getUserStatusTag = (isActive: boolean | undefined) => {
    if (isActive === false) {
      return <Tag color="red">{t('admin.blocked')}</Tag>
    }
    return <Tag color="green">{t('admin.active')}</Tag>
  }

  const showUserModal = (user: UserResponse) => {
    setSelectedUser(user)
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setSelectedUser(null)
    setSelectedRoles([])
  }

  const handleCheckboxChange = (rolename: string, checked: boolean) => {
    setSelectedRoles(prev => {
      if (checked) {
        if (!prev.includes(rolename)) {
          return [...prev, rolename]
        }
      } else {
        return prev.filter(name => name !== rolename)
      }
      return prev
    })
  }

  const handleOk = () => {
    if (selectedUser && selectedRoles.length > 0) {
      const roleNameString = selectedRoles.join(',')
      dispatch(
        RolesActions.updateUserRoles({
          userId: selectedUser.id,
          roleName: roleNameString,
        }),
      )
    }
    handleCloseModal()
  }

  const handleCaretClick = (e: React.MouseEvent, user: UserResponse) => {
    e.stopPropagation()
    dispatch(RolesActions.getUserRolesRequest(user.id))
    setExpandedUserId(prev => (prev === user.id ? null : user.id))
  }

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page)
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize)
    }
  }

  const handleBlockUser = async (user: UserResponse) => {
    try {
      await AdminUserService.Put.blockUser(user.id)
      message.success(t('admin.blockUserSuccess'))
      
      // Refresh user list
      dispatch(
        UsersActions.getUsersRequest({
          page: currentPage,
          pageSize: pageSize,
        }),
      )
    } catch (err) {
      console.error('Error blocking user:', err)
      message.error(t('admin.blockUserFailed'))
    }
  }

  const handleUnblockUser = async (user: UserResponse) => {
    try {
      await AdminUserService.Put.unblockUser(user.id)
      message.success(t('admin.unblockUserSuccess'))
      
      // Refresh user list
      dispatch(
        UsersActions.getUsersRequest({
          page: currentPage,
          pageSize: pageSize,
        }),
      )
    } catch (err) {
      console.error('Error unblocking user:', err)
      message.error(t('admin.unblockUserFailed'))
    }
  }

  return (
    <div className={styles.container}>
      <div className="table-container">
        {Array.isArray(users) && users.map(user => (
          <React.Fragment key={user.id}>
            <div
              className={`${styles.tableRow} ${
                expandedUserId === user.id ? styles.expanded : ''
              }`}
            >
              <div 
                className={styles.tableCell}
                onClick={() => showUserModal(user)}
                style={{ cursor: 'pointer' }}
              >
                {`${user.lastname ?? ''} ${user.firstname ?? ''}`}
              </div>
              <div 
                className={styles.tableCell}
                onClick={() => showUserModal(user)}
                style={{ cursor: 'pointer' }}
              >
                {user.email}
              </div>
              <div
                className={styles.tableCell}
                style={{ color: getInvitationStatusColor(user.invitationStatus) }}
                onClick={() => showUserModal(user)}
              >
                {user.invitationStatus || t('admin.unknown')}
              </div>
              <div
                className={styles.tableCell}
                onClick={() => showUserModal(user)}
                style={{ cursor: 'pointer' }}
              >
                {getUserStatusTag(user.isActive)}
              </div>
              <div
                onClick={e => handleCaretClick(e, user)}
                style={{ padding: '0 8px', cursor: 'pointer' }}
              >
                <CaretDownOutlined style={{ fontSize: 16 }} />
              </div>
              <div style={{ padding: '0 8px' }}>
                <Dropdown
                  overlay={
                    <Menu>
                      {user.isActive !== false ? (
                        <Menu.Item
                          key="block"
                          danger
                          onClick={(e) => {
                            e.domEvent.stopPropagation()
                            handleBlockUser(user)
                          }}
                        >
                          {t('admin.blockUser')}
                        </Menu.Item>
                      ) : (
                        <Menu.Item
                          key="unblock"
                          onClick={(e) => {
                            e.domEvent.stopPropagation()
                            handleUnblockUser(user)
                          }}
                        >
                          {t('admin.unblockUser')}
                        </Menu.Item>
                      )}
                    </Menu>
                  }
                  trigger={['click']}
                >
                  <Button
                    type="link"
                    icon={<MoreOutlined />}
                    onClick={e => e.stopPropagation()}
                  />
                </Dropdown>
              </div>
            </div>

            {/* Roles dưới row */}
            {expandedUserId === user.id && (
              <div className={styles.rolesSection}>
                <strong>{t('admin.roles')}:</strong>
                <ul>
                  {Array.isArray(rolesUserID) && rolesUserID.length > 0 ? (
                    rolesUserID.map((role, idx) => (
                      <li key={idx} className={styles.roleItem}>
                        <span>{role}</span>
                        <DeleteOutlined
                          className={styles.deleteIcon}
                          onClick={() => {
                            Modal.confirm({
                              title: t('admin.confirmDelete'),
                              content: t('admin.confirmDeleteRole', { role }),
                              okText: t('admin.delete'),
                              okType: 'danger',
                              cancelText: t('admin.cancel'),
                              onOk() {
                                dispatch(
                                  RolesActions.deleteUserRolesRequest({
                                    userId: user.id,
                                    roleName: [role],
                                  }),
                                )
                              },
                            })
                          }}
                        />
                      </li>
                    ))
                  ) : (
                    <li className={styles.noRole}>{t('admin.noRole')}</li>
                  )}
                </ul>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Pagination */}
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={totalUsers}
          onChange={handlePageChange}
          onShowSizeChange={handlePageChange}
          showSizeChanger
          pageSizeOptions={['10', '20', '50', '100']}
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} ${t('admin.of')} ${total} ${t('admin.users')}`
          }
        />
      </div>

      <Modal
        open={isModalVisible}
        onCancel={handleCloseModal}
        onOk={handleOk}
        title={t('admin.roleList')}
        centered
        width={600}
      >
        <div className={styles.roleModalWrapper}>
          <strong>{t('admin.selectRole')}:</strong>
          <div className={styles.checkboxGroup}>
            {Array.isArray(roleAdminData) && roleAdminData.map(role => (
              <div key={role.name} className={styles.checkboxItem}>
                <label>
                  <input
                    type="checkbox"
                    value={role.name}
                    checked={selectedRoles.includes(role.name)}
                    onChange={e =>
                      handleCheckboxChange(role.name, e.target.checked)
                    }
                  />
                  {role.name}
                </label>
              </div>
            ))}
          </div>
          <p className={styles.roleNote}>* {t('admin.onlyOneRole')}</p>
        </div>
      </Modal>
    </div>
  )
}